import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
export class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // Initialize cart type
    this.cartType = document.documentElement.dataset.cartDrawerType || 'theme';
    
    // Listen for ReCharge cart events
    if (this.cartType === 'recharge') {
      this.initializeReChargeCart();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * Initialize ReCharge cart
   */
  initializeReChargeCart() {
    if (window.ReChargeCart) {
      this.rechargeCart = window.ReChargeCart;
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      if (this.cartType === 'recharge') {
        // Let ReCharge handle the cart open
        return;
      }
      this.showDialog();
    }
  };

    open() {
    if (this.cartType === 'recharge') {
      const tryOpenReChargeCart = () => {
        // Try direct API calls first
        if (this.rechargeCart && typeof this.rechargeCart.open === 'function') {
          this.rechargeCart.open();
          return true;
        } else if (window['ReChargeCart'] && typeof window['ReChargeCart'].open === 'function') {
          window['ReChargeCart'].open();
          return true;
        } else if (window['ReCharge'] && typeof window['ReCharge'].openCart === 'function') {
          window['ReCharge'].openCart();
          return true;
        } else if (window['ReCharge'] && window['ReCharge'].cart && typeof window['ReCharge'].cart.open === 'function') {
          window['ReCharge'].cart.open();
          return true;
        }
        
        // Look for ReCharge's cart button
        const rechargeCartButton = document.querySelector('[data-recharge-cart-trigger], .recharge-cart-trigger, [data-recharge-action="open-cart"]');
        if (rechargeCartButton && rechargeCartButton instanceof HTMLElement) {
          rechargeCartButton.click();
          return true;
        }
        
        // Look for any cart button that's not add-to-cart
        const allButtons = document.querySelectorAll('button, a');
        for (const button of allButtons) {
          if (button instanceof HTMLElement) {
            const text = button.textContent?.toLowerCase() || '';
            const ariaLabel = button.getAttribute('aria-label')?.toLowerCase() || '';
            const className = button.className?.toLowerCase() || '';
            
            if ((text.includes('cart') || ariaLabel.includes('cart') || className.includes('cart')) &&
                !text.includes('add') && !text.includes('buy') && 
                !ariaLabel.includes('add') && !ariaLabel.includes('buy') &&
                !button.closest('cart-drawer-component')) {
              button.click();
              return true;
            }
          }
        }
        
        return false;
      };

      // Try immediately, then retry a few times
      if (tryOpenReChargeCart()) return;
      
      let attempts = 0;
      const retryOpen = () => {
        attempts++;
        if (tryOpenReChargeCart() || attempts >= 5) return;
        setTimeout(retryOpen, 200);
      };
      
      setTimeout(retryOpen, 200);
      return;
    }
    
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    if (this.cartType === 'recharge') {
      // Close ReCharge cart using their API
      if (this.rechargeCart) {
        this.rechargeCart.close();
      } else if (window['ReChargeCart']) {
        window['ReChargeCart'].close();
      }
      return;
    }
    
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
